import { useEffect, useState } from 'react'
import { Link, Navigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'

interface KnockoutArtist {
  id: string
  label: string
  safeTag: string
  seedOrder: number
  imageUrl: string | null
}

interface AdminKnockoutState {
  session: { id: string; status: string; startedAt: string | null; completedAt: string | null } | null
  player: { email: string; displayName: string | null; profileImageUrl: string | null } | null
  roster: KnockoutArtist[]
  picks: Array<{ id: string; pickIndex: number; stage: string; selected: KnockoutArtist | null; eliminated: KnockoutArtist | null; createdAt: string }>
  progress: { completed: number; total: number; progressLabel: string; stageLabel: string }
  currentMatchup: { progressLabel: string; stageLabel: string; artists: [KnockoutArtist, KnockoutArtist] } | null
  winner: KnockoutArtist | null
}

interface KnockoutPlayer {
  email: string
  displayName: string | null
  profileImageUrl: string | null
}

interface AdminKnockoutResponse {
  state?: AdminKnockoutState
  playerEmail?: string
  playerFound?: boolean
  players?: KnockoutPlayer[]
  error?: string
}

const DEMO_ROSTER: KnockoutArtist[] = [
  { id: '1', label: 'Hernán Cattáneo', safeTag: 'Progressive house', seedOrder: 1, imageUrl: null },
  { id: '2', label: 'Nick Warren', safeTag: 'Progressive', seedOrder: 2, imageUrl: null },
  { id: '3', label: 'Swedish House Mafia', safeTag: 'Big-room group', seedOrder: 3, imageUrl: null },
  { id: '4', label: 'Hardwell', safeTag: 'Big-room', seedOrder: 4, imageUrl: null },
  { id: '5', label: 'Guy J', safeTag: 'Progressive', seedOrder: 5, imageUrl: null },
  { id: '6', label: 'Guy Mantzur', safeTag: 'Progressive', seedOrder: 6, imageUrl: null },
  { id: '7', label: 'Eric Prydz', safeTag: 'Progressive', seedOrder: 7, imageUrl: null },
  { id: '8', label: 'Tiësto', safeTag: 'Trance / open-format', seedOrder: 8, imageUrl: null },
  { id: '9', label: 'Solomun', safeTag: 'Melodic house', seedOrder: 9, imageUrl: null },
  { id: '10', label: 'Avicii', safeTag: 'Progressive', seedOrder: 10, imageUrl: null },
  { id: '11', label: 'Tale of Us', safeTag: 'Afterlife', seedOrder: 11, imageUrl: null },
  { id: '12', label: 'deadmau5', safeTag: 'Progressive house', seedOrder: 12, imageUrl: null },
  { id: '13', label: 'Black Coffee', safeTag: 'Afro-house', seedOrder: 13, imageUrl: null },
  { id: '14', label: 'Carl Cox', safeTag: 'Techno', seedOrder: 14, imageUrl: null },
  { id: '15', label: 'Martin Garrix', safeTag: 'Big-room', seedOrder: 15, imageUrl: null },
  { id: '16', label: 'Boris Brejcha', safeTag: 'High-tech minimal', seedOrder: 16, imageUrl: null },
]

const DEMO_PLAYER: KnockoutPlayer = { email: 'member@example.com', displayName: 'Demo member', profileImageUrl: null }

const DEMO_STATE: AdminKnockoutState = {
  session: { id: 'local-preview', status: 'in_progress', startedAt: new Date().toISOString(), completedAt: null },
  player: DEMO_PLAYER,
  roster: DEMO_ROSTER,
  picks: [],
  progress: { completed: 0, total: 15, progressLabel: '01 / 08', stageLabel: 'Opening round' },
  currentMatchup: { progressLabel: '01 / 08', stageLabel: 'Opening round', artists: [DEMO_ROSTER[0], DEMO_ROSTER[1]] },
  winner: null,
}

function formatDate(value?: string | null) {
  if (!value) return 'Not started'
  return new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }).format(new Date(value))
}

function formatStatus(value: string) {
  return value.replace(/_/g, ' ').replace(/^\w/, char => char.toUpperCase())
}

export default function AdminKnockoutPage() {
  const { isLoggedIn, loading, status, rights } = useAuth()
  const devPreview = import.meta.env.DEV && new URLSearchParams(window.location.search).get('preview') === '1'
  const [state, setState] = useState<AdminKnockoutState | null>(null)
  const [players, setPlayers] = useState<KnockoutPlayer[]>(devPreview ? [DEMO_PLAYER] : [])
  const [playerEmail, setPlayerEmail] = useState(devPreview ? DEMO_PLAYER.email : '')
  const [loadingState, setLoadingState] = useState(true)
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [lastUpdatedAt, setLastUpdatedAt] = useState<string | null>(null)

  const canAdmin = devPreview || (isLoggedIn && status === 'approved' && rights['admin.knockout.manage'] === true)

  async function loadKnockout(email = playerEmail) {
    const targetEmail = email.trim().toLowerCase()
    if (devPreview) {
      setState(DEMO_STATE)
      setPlayers([DEMO_PLAYER])
      setPlayerEmail(DEMO_PLAYER.email)
      setLoadingState(false)
      setLastUpdatedAt(new Date().toISOString())
      return
    }
    setLoadingState(true)
    setError(null)
    try {
      const query = targetEmail ? `?email=${encodeURIComponent(targetEmail)}` : ''
      const res = await fetch(`/api/admin/knockout${query}`, { credentials: 'include' })
      const data = await res.json() as AdminKnockoutResponse
      if (!res.ok) throw new Error(data.error ?? 'Unable to load Knockout admin')
      setPlayers(data.players ?? [])
      setState(data.state ?? null)
      setPlayerEmail(data.playerEmail ?? data.state?.player?.email ?? targetEmail)
      if (data.playerFound === false) setError('No approved Knockout player found for that email.')
      setLastUpdatedAt(new Date().toISOString())
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to load Knockout admin')
    } finally {
      setLoadingState(false)
    }
  }

  useEffect(() => {
    if (canAdmin) void loadKnockout()
  }, [canAdmin])

  async function resetSession() {
    const targetEmail = playerEmail.trim().toLowerCase()
    const targetName = state?.player?.displayName || state?.player?.email || targetEmail || 'this member'
    if (devPreview) {
      setMessage('Local preview reset. Production reset still requires admin auth.')
      setState(DEMO_STATE)
      return
    }
    if (!targetEmail) {
      setError('Select a member email before resetting a Knockout Session.')
      return
    }
    if (!window.confirm(`Reset ${targetName}'s Knockout Session? This clears current progress and returns them to the ready screen.`)) return
    setBusy(true)
    setMessage(null)
    setError(null)
    try {
      const res = await fetch('/api/admin/knockout', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ action: 'reset', email: targetEmail }),
      })
      const data = await res.json() as AdminKnockoutResponse
      if (!res.ok) throw new Error(data.error ?? 'Unable to reset session')
      setState(data.state ?? null)
      setPlayerEmail(data.playerEmail ?? targetEmail)
      setLastUpdatedAt(new Date().toISOString())
      setMessage(`Knockout Session reset. ${targetName} will land on the ready screen.`)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to reset session')
    } finally {
      setBusy(false)
    }
  }

  if (!devPreview && loading) return <div className="admin-page"><p className="admin-muted">Checking Knockout admin access...</p></div>
  if (!devPreview && !isLoggedIn) return <Navigate to="/signin" replace />

  if (!canAdmin) {
    return (
      <div className="admin-page">
        <span className="legal-eyebrow">Admin</span>
        <h1 className="admin-heading">Admin access required.</h1>
        <p className="admin-muted">This page is only available to approved Yan-Vada admins.</p>
        <Link to="/" className="btn btn-secondary">Back to public home</Link>
      </div>
    )
  }

  const statusLabel = state?.winner ? 'Completed' : state?.session?.status ?? 'Not started'
  const nextPair = state?.currentMatchup?.artists.map(artist => artist.label).join(' vs ') ?? 'No active matchup'
  const playerLabel = state?.player?.displayName || state?.player?.email || playerEmail || 'Selected member'

  return (
    <div className="admin-page admin-knockout-page">
      <header className="admin-header admin-knockout-header">
        <div>
          <span className="legal-eyebrow">Admin · Knockout Session</span>
          <h1 className="admin-heading">Knockout Session</h1>
          <p className="admin-muted">Preview, reset, and check any approved member's Knockout game state.</p>
          {lastUpdatedAt && <p className="admin-muted admin-knockout-updated">Last updated {formatDate(lastUpdatedAt)}</p>}
        </div>
        <div className="admin-member-actions">
          <Link to={devPreview ? '/knockout?preview=1' : '/knockout'} className="btn btn-primary">Open /knockout</Link>
          <button className="btn btn-secondary" type="button" disabled={busy || loadingState} onClick={() => void loadKnockout()}>Refresh state</button>
          <button className="btn btn-danger" type="button" disabled={busy || loadingState || !playerEmail} onClick={() => void resetSession()}>Reset selected session</button>
        </div>
      </header>

      {(message || error) && (
        <div className={`admin-alert${error ? ' admin-alert-error' : ''}`} role={error ? 'alert' : 'status'}>
          {error ?? message}
        </div>
      )}

      <form
        className="admin-card admin-knockout-player"
        onSubmit={(event) => {
          event.preventDefault()
          void loadKnockout(playerEmail)
        }}
      >
        <div>
          <span className="legal-eyebrow">Player target</span>
          <h2>Select member</h2>
          <p className="admin-muted">Load an approved member by email before reviewing or resetting their bracket.</p>
        </div>
        <label>
          <span>Player email</span>
          <input
            list="admin-knockout-player-list"
            type="email"
            value={playerEmail}
            placeholder="member@example.com"
            onChange={event => setPlayerEmail(event.currentTarget.value)}
          />
        </label>
        <datalist id="admin-knockout-player-list">
          {players.map(player => <option key={player.email} value={player.email} label={player.displayName ?? player.email} />)}
        </datalist>
        <button className="btn btn-secondary" type="submit" disabled={loadingState || busy}>Load player</button>
      </form>

      {loadingState ? (
        <section className="admin-card admin-knockout-warning"><p className="admin-muted">Loading Knockout state...</p></section>
      ) : (
        <>
          <section className="admin-card admin-knockout-current">
            <div>
              <span className="legal-eyebrow">Current state</span>
              <h2>{state?.winner ? `Winner: ${state.winner.label}` : nextPair}</h2>
              <p className="admin-muted">{state?.currentMatchup ? `${state.currentMatchup.stageLabel} · ${state.currentMatchup.progressLabel}` : 'Ready screen or final reveal.'}</p>
            </div>
            <dl className="admin-rights-grid admin-knockout-meta">
              <div><dt>Player</dt><dd>{playerLabel}</dd></div>
              <div><dt>Progress</dt><dd>{state?.progress.completed ?? 0}/{state?.progress.total ?? 15}</dd></div>
              <div><dt>Status</dt><dd>{formatStatus(statusLabel)}</dd></div>
              <div className="admin-knockout-email"><dt>Player email</dt><dd>{state?.player?.email ?? (playerEmail || 'Not selected')}</dd></div>
              <div><dt>Started</dt><dd>{formatDate(state?.session?.startedAt)}</dd></div>
              <div><dt>Completed</dt><dd>{formatDate(state?.session?.completedAt)}</dd></div>
              <div><dt>Last picks</dt><dd>{state?.picks.length ?? 0}</dd></div>
            </dl>
          </section>

          <section className="admin-card admin-knockout-warning">
            <strong>Design/admin note</strong>
            <p>Broader admin redesign is queued for Claude Design. DJ images remain seed/reference assets until Riz/PJ clear publishing rights.</p>
          </section>

          {(state?.picks.length ?? 0) > 0 && (
            <section className="admin-card admin-knockout-picks">
              <div className="admin-knockout-section-head">
                <div>
                  <span className="legal-eyebrow">Pick path</span>
                  <h2>{playerLabel}'s bracket so far</h2>
                </div>
                <span className="admin-muted">{state?.picks.length ?? 0} locked</span>
              </div>
              <div className="admin-knockout-pick-list">
                {state?.picks.map(pick => (
                  <article key={pick.id}>
                    <span>{String(pick.pickIndex).padStart(2, '0')}</span>
                    <div>
                      <strong>{pick.selected?.label ?? 'Unknown'} over {pick.eliminated?.label ?? 'Unknown'}</strong>
                      <small>{formatStatus(pick.stage)} · {formatDate(pick.createdAt)}</small>
                    </div>
                  </article>
                ))}
              </div>
            </section>
          )}

          <section className="admin-card admin-knockout-roster">
            <div className="admin-knockout-section-head">
              <div>
                <span className="legal-eyebrow">Roster</span>
                <h2>Seeded bracket order</h2>
              </div>
              <span className="admin-muted">16 seed entries</span>
            </div>
            <div className="admin-knockout-roster-grid">
              {(state?.roster ?? []).map(artist => (
                <article key={artist.id} className="admin-knockout-artist">
                  <span>{String(artist.seedOrder).padStart(2, '0')}</span>
                  <div>
                    <strong>{artist.label}</strong>
                    <small>{artist.safeTag}{artist.imageUrl ? '' : ' · image pending rights'}</small>
                  </div>
                </article>
              ))}
            </div>
          </section>
        </>
      )}
    </div>
  )
}
